//
// Imports
//

import NProgress from "nprogress";

//
// Types
//

declare global
{
	interface DocumentEventMap
	{
		"donutteam:pjaxAbort": CustomEvent<null>;
		"donutteam:pjaxEnd": CustomEvent<null>;
		"donutteam:pjaxError": CustomEvent<null>;
		"donutteam:pjaxLoad": CustomEvent<PjaxLoadEventDetails>;
		"donutteam:pjaxProgress": CustomEvent<null>;
		"donutteam:pjaxStart": CustomEvent<null>;
	}
}; 

type PjaxLoadAction = "push" | "pop";

type PjaxLoadEventDetails =
{
	action: PjaxLoadAction;
	xhr: XMLHttpRequest;
};

//
// Locals
//

function handleAnchorClick(anchor: HTMLAnchorElement, event: MouseEvent)
{
	// Note: Want CTRL/CMD+Click to work as normal
	if (event.ctrlKey || event.metaKey)
	{
		return;
	}

	event.preventDefault();

	pjaxLoad(anchor.href, "push");
}

function upgradeAnchors() : void
{
	const anchors = document.querySelectorAll("a:not(.pjax):not(.no-pjax)") as NodeListOf<HTMLAnchorElement>;

	if (anchors.length == 0)
	{
		return;
	}

	console.log("[Pjax] Processing " + anchors.length + " unprocessed anchors...");

	const upgradableAnchors : HTMLAnchorElement[] = [];

	for (const anchor of anchors)
	{
		const isSameOrigin = anchor.href.startsWith(window.location.origin);

		const isTargetingSelf = anchor.target == "" || anchor.target == "_self";

		if (!isSameOrigin || !isTargetingSelf)
		{
			anchor.classList.add("no-pjax");

			continue;
		}

		upgradableAnchors.push(anchor);
	}

	console.log("[Pjax] Upgrading " + upgradableAnchors.length + " anchors...");

	for (const anchor of upgradableAnchors)
	{
		anchor.classList.add("pjax");

		anchor.addEventListener("click", (event) => handleAnchorClick(anchor, event));
	}
}

function replaceMainElementHtml(html: string)
{
	const domParser = new DOMParser();

	const newDocument = domParser.parseFromString(html, "text/html");

	const newMainElement = newDocument.querySelector("main.content-wrapper");

	if (newMainElement == null)
	{
		throw new Error("Could not find main.content-wrapper in response HTML.");
	}

	const currentMainElement = document.querySelector("main.content-wrapper");

	if (currentMainElement == null)
	{
		throw new Error("Could not find main.content-wrapper in current document.");
	}

	currentMainElement.replaceWith(newMainElement);
}

let xhr: XMLHttpRequest | null = null;

function pjaxLoad(href: string, action: PjaxLoadAction): void
{
	//
	// Create XHR Event Handlers
	//

	const onAbort = () =>
	{
		const pjaxAbortEvent = new CustomEvent("donutteam:pjaxAbort");

		document.dispatchEvent(pjaxAbortEvent);
	};

	const onError = () =>
	{
		const pjaxErrorEvent = new CustomEvent("donutteam:pjaxError");

		document.dispatchEvent(pjaxErrorEvent);
	};

	const onLoad = () =>
	{
		const pjaxLoadEvent = new CustomEvent<PjaxLoadEventDetails>("donutteam:pjaxLoad",
			{
				detail:
				{
					action,
					xhr: xhr!,
				},
			});

		document.dispatchEvent(pjaxLoadEvent);
	};

	const onLoadEnd = () =>
	{
		const pjaxEndEvent = new CustomEvent<null>("donutteam:pjaxEnd");

		document.dispatchEvent(pjaxEndEvent);
	};

	const onLoadStart = () =>
	{
		const pjaxStartEvent = new CustomEvent<null>("donutteam:pjaxStart");

		document.dispatchEvent(pjaxStartEvent);
	};

	const onProgress = () =>
	{
		const pjaxProgressEvent = new CustomEvent<null>("donutteam:pjaxProgress");

		document.dispatchEvent(pjaxProgressEvent);
	};

	//
	// Abort Previous Request
	//

	if (xhr != null && xhr.readyState != XMLHttpRequest.DONE)
	{
		xhr.abort();
	}

	//
	// Request Page
	//

	xhr = new XMLHttpRequest();

	xhr.open("GET", href, true);

	xhr.setRequestHeader("X-DONUTTEAM-PJAX", "true");

	xhr.addEventListener("loadstart", onLoadStart);
	xhr.addEventListener("progress", onProgress);
	xhr.addEventListener("loadend", onLoadEnd);

	xhr.addEventListener("abort", onAbort);

	xhr.addEventListener("error", onError);
	xhr.addEventListener("timeout", onError);

	xhr.addEventListener("load", onLoad);

	xhr.send();
}

//
// Utility Functions
//

export function initialisePjax(onLoad: () => void): void
{
	console.log("[Pjax] Initialising...");

	upgradeAnchors();

	document.addEventListener("donutteam:pjaxStart", () => NProgress.start());

	document.addEventListener("donutteam:pjaxProgress", () => NProgress.inc());

	document.addEventListener("donutteam:pjaxEnd", () => NProgress.done());

	document.addEventListener("donutteam:pjaxAbort", () => NProgress.done());

	document.addEventListener("donutteam:pjaxError", (event) => console.error("donutteam:pjaxError", event));

	document.addEventListener("donutteam:pjaxLoad",
		(event) =>
		{
			const html = event.detail.xhr.responseText;

			replaceMainElementHtml(html);

			const url = event.detail.xhr.responseURL;

			if (url != null && event.detail.action == "push")
			{
				window.history.pushState(null, "", url);
			}

			upgradeAnchors();

			onLoad();

			document.title = document.querySelector("main.content-wrapper")?.getAttribute("data-title") ?? "Untitled page | Donut Team";

			window.scrollTo(0, 0);
		});

	window.addEventListener("popstate",
		() =>
		{
			pjaxLoad(window.location.href, "pop");
		});
}